import { useMemo, useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactSection() {
  const { ref: sectionRef, isVisible } = useScrollReveal<HTMLElement>();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    privacyAgreed: false,
  });
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitError, setSubmitError] = useState('');

  const services = [
    { id: 'consulting', label: '人材育成コンサルティング' },
    { id: 'engineering', label: 'システムエンジニアリング' },
    { id: 'web', label: 'Web制作・デジタルコンテンツ' },
  ];

  const selectedServiceLabels = useMemo(
    () => services.filter((service) => selectedServices.includes(service.id)).map((service) => service.label),
    [selectedServices]
  );

  const toggleService = (id: string) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((serviceId) => serviceId !== id) : [...prev, id]
    );
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name.trim()) nextErrors.name = 'お名前を入力してください';
    if (!formData.email.trim()) {
      nextErrors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = '正しいメールアドレスを入力してください';
    }
    if (!formData.message.trim()) nextErrors.message = 'お問い合わせ内容を入力してください';
    if (selectedServices.length === 0) nextErrors.services = '興味のあるサービスを1つ以上選択してください';
    if (!formData.privacyAgreed) nextErrors.privacy = 'プライバシーポリシーに同意してください';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      message: '',
      privacyAgreed: false,
    });
    setSelectedServices([]);
    setErrors({});
  };

  const handleOpenConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setIsConfirmOpen(false);
    setSubmitStatus('submitting');
    setSubmitError('');

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('company', formData.company);
      body.append('message', formData.message);
      body.append('services', selectedServiceLabels.join(' / '));
      body.append('privacy', formData.privacyAgreed ? '同意済み' : '');

      const response = await fetch('/send.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: body.toString(),
      });

      const text = await response.text();
      if (!response.ok || text.trim() !== 'OK') {
        const normalized = text.trim();
        const safeMessage =
          normalized === '必須項目が未入力です。'
            ? normalized
            : '入力内容をご確認のうえ、時間を空けて再度お試しください。お急ぎの場合はお電話にてお問い合わせください。';
        throw new Error(safeMessage);
      }

      setSubmitStatus('success');
      resetForm();
    } catch (error) {
      setSubmitStatus('error');
      setSubmitError(error instanceof Error ? error.message : '送信に失敗しました。');
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="bg-axelia-bg-warm py-20 md:py-28 lg:py-32">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <div className={`relative aspect-[21/9] md:aspect-[3/1] rounded-[1.5rem] overflow-hidden mb-10 md:mb-14 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20architectural%20interior%20with%20dramatic%20natural%20light%20beams%20in%20warm%20neutral%20tones%2C%20sophisticated%20modern%20space%20with%20soft%20shadows%20and%20minimal%20aesthetic%2C%20cream%20and%20charcoal%20color%20palette%2C%20elegant%20composition%20suitable%20for%20contact%20section%20banner%20of%20premium%20technology%20company&width=1200&height=400&seq=axelia-contact-01&orientation=landscape"
              alt="お問い合わせ"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-axelia-text/20" />
          </div>

          <div className={`text-center mb-10 md:mb-12 transition-all duration-700 delay-100 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-axelia-text leading-snug mb-4">
              まずは、
              <span className="italic font-normal text-axelia-text-secondary">気軽に</span>
              お話ししましょう
            </h2>
            <p className="text-sm md:text-base text-axelia-text-secondary leading-[1.9] max-w-xl mx-auto">
              人材育成コンサルティングやシステム開発、Web制作に関するご相談は、
              以下のフォームよりお気軽にお問い合わせください。
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm text-axelia-text-secondary">
              <a href="mailto:customer@axelia-tech.com" className="inline-flex items-center gap-2 hover:text-axelia-accent transition-colors">
                <i className="ri-mail-line" />
                <span>customer@axelia-tech.com</span>
              </a>
              <a href="tel:0368217456" className="inline-flex items-center gap-2 hover:text-axelia-accent transition-colors">
                <i className="ri-phone-line" />
                <span>03-6821-7456</span>
              </a>
            </div>
          </div>

          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <form onSubmit={handleOpenConfirm} className="bg-white rounded-2xl p-6 md:p-10 border border-transparent">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-axelia-text mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-4 py-3 bg-axelia-bg rounded-xl text-sm text-axelia-text placeholder:text-axelia-text-muted border ${
                      errors.name ? 'border-red-400' : 'border-transparent focus:border-axelia-accent/30'
                    } focus:outline-none transition-colors`}
                    placeholder="山田 太郎"
                  />
                  {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-axelia-text mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-4 py-3 bg-axelia-bg rounded-xl text-sm text-axelia-text placeholder:text-axelia-text-muted border ${
                      errors.email ? 'border-red-400' : 'border-transparent focus:border-axelia-accent/30'
                    } focus:outline-none transition-colors`}
                    placeholder="example@email.com"
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="contact-company" className="block text-sm font-medium text-axelia-text mb-2">
                    会社名
                  </label>
                  <input
                    type="text"
                    id="contact-company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 bg-axelia-bg rounded-xl text-sm text-axelia-text placeholder:text-axelia-text-muted border border-transparent focus:border-axelia-accent/30 focus:outline-none transition-colors"
                    placeholder="株式会社○○"
                  />
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-axelia-text mb-3">
                  興味のあるサービス <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {services.map((service) => (
                    <label
                      key={service.id}
                      className={`inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full text-sm cursor-pointer transition-all duration-300 border ${
                        selectedServices.includes(service.id)
                          ? 'bg-axelia-text text-white border-axelia-text'
                          : 'bg-axelia-bg text-axelia-text-secondary border-transparent hover:border-axelia-accent/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedServices.includes(service.id)}
                        onChange={() => toggleService(service.id)}
                        className="sr-only"
                      />
                      <span
                        className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                          selectedServices.includes(service.id) ? 'bg-white border-white' : 'border-axelia-text-muted'
                        }`}
                      >
                        {selectedServices.includes(service.id) && <i className="ri-check-line text-xs text-axelia-text" />}
                      </span>
                      {service.label}
                    </label>
                  ))}
                </div>
                {errors.services && <p className="text-xs text-red-500 mt-2">{errors.services}</p>}
              </div>

              <div className="mb-5">
                <label htmlFor="contact-message" className="block text-sm font-medium text-axelia-text mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  rows={5}
                  maxLength={500}
                  className={`w-full px-4 py-3 bg-axelia-bg rounded-xl text-sm text-axelia-text placeholder:text-axelia-text-muted border ${
                    errors.message ? 'border-red-400' : 'border-transparent focus:border-axelia-accent/30'
                  } focus:outline-none transition-colors resize-none`}
                  placeholder="お問い合わせ内容を具体的にご記入ください"
                />
                <div className="flex items-center justify-between mt-1.5">
                  {errors.message && <p className="text-xs text-red-500">{errors.message}</p>}
                  <p className={`text-xs ml-auto ${formData.message.length >= 450 ? 'text-axelia-accent' : 'text-axelia-text-muted'}`}>
                    {formData.message.length}/500文字
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <label className="inline-flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.privacyAgreed}
                    onChange={(e) => setFormData({ ...formData, privacyAgreed: e.target.checked })}
                    className="sr-only"
                  />
                  <span
                    className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                      formData.privacyAgreed ? 'bg-axelia-text border-axelia-text' : 'border-axelia-text-muted'
                    }`}
                  >
                    {formData.privacyAgreed && <i className="ri-check-line text-sm text-white" />}
                  </span>
                  <span className="text-sm text-axelia-text-secondary leading-relaxed">
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-axelia-accent transition-colors">
                      プライバシーポリシー
                    </a>
                    に同意します <span className="text-red-500">*</span>
                  </span>
                </label>
                {errors.privacy && <p className="text-xs text-red-500 mt-2">{errors.privacy}</p>}
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  className="px-12 py-4 bg-axelia-text text-white text-sm font-medium rounded-full hover:bg-axelia-accent transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  送信する
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {isConfirmOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white p-6 md:p-8 shadow-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-axelia-text-muted mb-3">Confirm</p>
            <h3 className="font-serif text-2xl font-bold text-axelia-text mb-2">入力内容の確認</h3>
            <p className="text-sm text-axelia-text-secondary mb-6">送信前に内容をご確認ください。</p>

            <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
              <div className="rounded-2xl bg-axelia-bg p-4">
                <p className="text-xs text-axelia-text-muted mb-1">お名前</p>
                <p className="text-sm text-axelia-text">{formData.name}</p>
              </div>
              <div className="rounded-2xl bg-axelia-bg p-4">
                <p className="text-xs text-axelia-text-muted mb-1">メールアドレス</p>
                <p className="text-sm text-axelia-text break-all">{formData.email}</p>
              </div>
              <div className="rounded-2xl bg-axelia-bg p-4">
                <p className="text-xs text-axelia-text-muted mb-1">会社名</p>
                <p className="text-sm text-axelia-text">{formData.company || '未入力'}</p>
              </div>
              <div className="rounded-2xl bg-axelia-bg p-4">
                <p className="text-xs text-axelia-text-muted mb-1">興味のあるサービス</p>
                <p className="text-sm text-axelia-text">{selectedServiceLabels.join(' / ')}</p>
              </div>
              <div className="rounded-2xl bg-axelia-bg p-4">
                <p className="text-xs text-axelia-text-muted mb-1">お問い合わせ内容</p>
                <p className="text-sm text-axelia-text whitespace-pre-wrap">{formData.message}</p>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setIsConfirmOpen(false)}
                className="px-6 py-3 rounded-full border border-axelia-text/20 text-sm font-medium text-axelia-text hover:bg-axelia-bg transition-colors"
              >
                戻る
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-6 py-3 rounded-full bg-axelia-text text-sm font-medium text-white hover:bg-axelia-accent transition-colors"
              >
                この内容で送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {submitStatus === 'submitting' && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
            <div className="mx-auto mb-5 h-14 w-14 animate-spin rounded-full border-4 border-axelia-bg-warm border-t-axelia-text" />
            <h3 className="font-serif text-2xl font-bold text-axelia-text mb-2">送信中</h3>
            <p className="text-sm text-axelia-text-secondary">お問い合わせ内容を送信しています。</p>
          </div>
        </div>
      )}

      {submitStatus === 'success' && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <i className="ri-check-line text-3xl" />
            </div>
            <h3 className="font-serif text-2xl font-bold text-axelia-text mb-2">送信完了</h3>
            <p className="text-sm text-axelia-text-secondary mb-6">お問い合わせありがとうございます。内容を確認の上、担当者よりご連絡いたします。</p>
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="px-6 py-3 rounded-full bg-axelia-text text-sm font-medium text-white hover:bg-axelia-accent transition-colors"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-500">
              <i className="ri-close-line text-3xl" />
            </div>
            <h3 className="font-serif text-2xl font-bold text-axelia-text mb-2">送信失敗</h3>
            <p className="text-sm text-axelia-text-secondary mb-2">送信に失敗しました。</p>
            <p className="text-xs text-red-500 mb-6">{submitError}</p>
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="px-6 py-3 rounded-full bg-axelia-text text-sm font-medium text-white hover:bg-axelia-accent transition-colors"
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
